import { supabase } from './db';
import { logger } from '@/utils/logger';
import type { User } from '@/lib/types/auth';
import { UserRole, ActiveStatus } from '@/lib/types/enums';

// Define AuthResponse interface locally since it's not exported from types
interface AuthResponse {
  user: User;
  session: any;
}

// Helper function to detect Netlify environment
const isNetlifyEnvironment = () => {
  return typeof window !== 'undefined' && 
    (window.location.hostname.includes('netlify.app') || 
     document.referrer.includes('netlify.app'));
};

// Demo accounts for development
const DEMO_ACCOUNTS = {
  admin: {
    email: 'admin@flexhelp.com',
    password: 'admin123',
    role: 'admin',
    name: 'Admin User',
  },
  manager: {
    email: 'manager@flexhelp.com',
    password: 'manager123',
    role: 'property_manager',
    name: 'Property Manager',
  },
  owner: {
    email: 'owner@flexhelp.com',
    password: 'owner123',
    role: 'property_owner',
    name: 'Property Owner',
  },
  helper: {
    email: 'helper@flexhelp.com',
    password: 'helper123',
    role: 'flex_helper',
    name: 'Flex Helper',
  },
  // Demo account for easy login on Netlify
  demo: {
    email: 'lewis@smartprofyl.com',
    password: 'password123',
    role: 'property_owner',
    name: 'Demo User',
  },
};

export async function login(email: string, password: string): Promise<User> {
  try {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    // Check for demo accounts first
    const demoAccount = Object.values(DEMO_ACCOUNTS).find(
      account => account.email === email && account.password === password
    );

    // Always use demo accounts on Netlify environment
    if (isNetlifyEnvironment() || process.env.NODE_ENV === 'production') {
      // On Netlify, we'll allow any email with the demo password to work
      if (password === 'password123' || password === 'demo123') {
        // Use the demo account or create a custom one based on the email
        const user: User = {
          id: crypto.randomUUID(),
          email: email,
          name: email.split('@')[0] || 'Demo User',
          role: 'property_owner' as UserRole,
          status: 'active' as ActiveStatus,
          createdAt: new Date().toISOString(),
          avatar: null,
        };
        
        // Store in localStorage to persist the session
        localStorage.setItem('flexhelp_user', JSON.stringify(user));
        return user;
      }
      
      // If it's a known demo account, use it
      if (demoAccount) {
        const user: User = {
          id: crypto.randomUUID(),
          email: demoAccount.email,
          name: demoAccount.name,
          role: demoAccount.role as UserRole,
          status: 'active' as ActiveStatus,
          createdAt: new Date().toISOString(),
          avatar: null,
        };
        
        // Store in localStorage to persist the session
        localStorage.setItem('flexhelp_user', JSON.stringify(user));
        return user;
      }
    }

    if (demoAccount) {
      const user: User = {
        id: crypto.randomUUID(),
        email: demoAccount.email,
        name: demoAccount.name,
        role: demoAccount.role as UserRole,
        status: 'active' as ActiveStatus,
        createdAt: new Date().toISOString(),
      };

      // Store demo user data in localStorage
      localStorage.setItem('demo_token', 'demo_token');
      localStorage.setItem('demo_user', JSON.stringify(user));

      return user;
    }

    // If not a demo account, proceed with Supabase auth
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      logger.error('Supabase auth error:', error);
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password');
      }
      throw new Error(error.message);
    }

    if (!data?.user) {
      throw new Error('No user data returned');
    }

    // Get user profile data
    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('*')
      .eq('id', data.user.id)
      .single();

    if (profileError) {
      logger.error('Error fetching user profile:', profileError);
      throw new Error('Failed to fetch user profile');
    }

    // Verify user status
    if (profile.status !== 'active') {
      throw new Error('Account is not active');
    }

    return {
      id: data.user.id,
      email: data.user.email!,
      name: profile.name,
      role: profile.role as UserRole,
      status: profile.status as ActiveStatus,
      createdAt: profile.created_at,
      avatar: profile.avatar_url,
    };
  } catch (error) {
    logger.error('Login error:', error);
    throw error;
  }
}

export async function register(data: { 
  email: string; 
  password: string; 
  name: string; 
  role: string;
}): Promise<User> {
  try {
    if (!data.email || !data.password || !data.name || !data.role) {
      throw new Error('All fields are required');
    }

    // Create auth user
    const { data: authData, error: signUpError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          name: data.name,
          role: data.role,
        },
      },
    });

    if (signUpError) {
      throw signUpError;
    }

    if (!authData?.user) {
      throw new Error('Failed to create user');
    }

    // Create user profile
    const { data: profile, error: profileError } = await supabase
      .from('users')
      .insert([{
        id: authData.user.id,
        email: data.email,
        name: data.name,
        role: data.role,
        status: 'active',
      }])
      .select()
      .single();

    if (profileError) {
      // Cleanup auth user if profile creation fails
      await supabase.auth.admin.deleteUser(authData.user.id);
      throw profileError;
    }

    return {
      id: authData.user.id,
      email: data.email,
      name: data.name,
      role: data.role as UserRole,
      status: 'active' as ActiveStatus,
      createdAt: new Date().toISOString(),
    };
  } catch (error) {
    logger.error('Registration error:', error);
    throw error;
  }
}

export async function logout() {
  // Clear demo user data
  localStorage.removeItem('demo_token');
  localStorage.removeItem('demo_user');
  localStorage.removeItem('flexhelp_user');

  // Also sign out from Supabase if needed
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    logger.error('Logout error:', error);
    throw error;
  }
}

export async function getCurrentSession() {
  try {
    // Check for demo token first
    const demoToken = localStorage.getItem('demo_token');
    if (demoToken) {
      return {
        access_token: demoToken,
        expires_at: Date.now() + 24 * 60 * 60 * 1000,
      };
    }

    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  } catch (error) {
    logger.error('Get session error:', error);
    return null;
  }
}

export async function getCurrentUser(): Promise<User | null> {
  try {
    // Check for demo user first
    const demoUserJson = localStorage.getItem('demo_user');
    const flexhelpUserJson = localStorage.getItem('flexhelp_user');
    if (demoUserJson) {
      return JSON.parse(demoUserJson);
    }
    if (flexhelpUserJson) {
      return JSON.parse(flexhelpUserJson);
    }

    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) return null;

    // Get user profile data
    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .single();

    if (profileError) {
      logger.error('Error fetching user profile:', profileError);
      return null;
    }

    return {
      id: user.id,
      email: user.email!,
      name: profile.name,
      role: profile.role as UserRole,
      status: profile.status as ActiveStatus,
      createdAt: profile.created_at,
      avatar: profile.avatar_url,
    };
  } catch (error) {
    logger.error('Get current user error:', error);
    return null;
  }
}

export function onAuthStateChange(callback: (user: User | null) => void) {
  // Monitor localStorage changes for demo mode
  const handleStorageChange = () => {
    const userJson = localStorage.getItem('demo_user');
    const flexhelpUserJson = localStorage.getItem('flexhelp_user');
    callback(userJson ? JSON.parse(userJson) : flexhelpUserJson ? JSON.parse(flexhelpUserJson) : null);
  };
  window.addEventListener('storage', handleStorageChange);
  
  // Initial check
  const userJson = localStorage.getItem('demo_user');
  const flexhelpUserJson = localStorage.getItem('flexhelp_user');
  if (userJson) {
    callback(JSON.parse(userJson));
  } else if (flexhelpUserJson) {
    callback(JSON.parse(flexhelpUserJson));
  }
  
  // Also listen for Supabase auth changes
  return supabase.auth.onAuthStateChange(async (event, session) => {
    try {
      if (event === 'SIGNED_IN' && session?.user) {
        const { data: profile, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (error) throw error;

        callback({
          id: session.user.id,
          email: session.user.email!,
          name: profile.name,
          role: profile.role as UserRole,
          status: profile.status as ActiveStatus,
          createdAt: profile.created_at,
          avatar: profile.avatar_url,
        });
      } else if (event === 'SIGNED_OUT') {
        callback(null);
      }
    } catch (error) {
      logger.error('Auth state change error:', error);
      callback(null);
    }
  });
}